import { Grid, makeStyles } from '@material-ui/core'
import BetaBadge from 'components/common/BetaBadge'
import DazzedHeading18 from 'components/common/text/DazzedHeading18'
import { OralHygieneListTypes, OralHygieneWidgets } from 'consts/oralHygieneReportConsts'
import _ from 'lodash'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'utils/analyticsUtils'
import DateFilter from '../components/CopilotControlHeader/DateFilter'
import CopilotReportsNav from './CopilotReportsNav'
import PatientsListRestricted from '../components/CopilotPatientsLists/PatientsListRestricted'
import { useCanViewCopilotPatients } from '../hooks/useCopilotAccountOwnerId'
import useOralHygieneReport from './hooks/useOralHygieneReport'
import { getOralHygieneSeePatientsTarget } from './oralHygieneReportLogic'
import OralHygienePatientsList from './patientsList/OralHygienePatientsList'
import HygieneTrendWidget from './widgets/HygieneTrendWidget'
import OralHygieneNumberCard from './widgets/OralHygieneNumberCard'
import CavityRiskIcon from './widgets/icons/CavityRiskIcon'
import HygieneAttentionIcon from './widgets/icons/HygieneAttentionIcon'
import PerioRiskIcon from './widgets/icons/PerioRiskIcon'

const useStyles = makeStyles(() => ({
  // Figma frame 218: the reports rail sits at the far left, the report content fills the rest.
  page: {
    background: 'var(--copilot-bg-color)',
    borderTop: '1px solid #E6E6E6',
    boxSizing: 'border-box',
    width: '100%',
    height: 'calc(100% - var(--app-header-v2-height))',
    display: 'flex',
    flexDirection: 'row'
  },
  root: {
    flexGrow: 1,
    minWidth: 0,
    height: '100%',
    padding: '0px 40px',
    display: 'flex',
    flexDirection: 'column'
  },
  // Shares CopilotControlHeader's outer box (design review: same title spacing on both reports):
  // 15px top margin and the fixed --copilot-header-height, title row directly over the date filter.
  header: {
    flexShrink: 0,
    marginTop: 15,
    height: 'var(--copilot-header-height)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 8
  },
  title: {
    fontWeight: 600
  },
  betaBadge: {
    marginLeft: 16
  },
  dateFilterContainer: {
    marginLeft: 5,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap'
  },
  widgetsContainer: {
    marginTop: 20,
    flexGrow: 1,
    minHeight: 0,
    paddingBottom: 20
  },
  // The cards have a fixed minimum, so on short viewports the column scrolls instead of the trend
  // widget silently clipping its legend.
  leftColumn: {
    height: '100%',
    overflowY: 'auto'
  },
  trendWidgetContainer: {
    flexGrow: 1,
    // Keep the chart, title, and legend visible when taller number cards require scrolling.
    minHeight: 220,
    display: 'flex',
    flexDirection: 'column'
  },
  numberCardContainer: {
    flexShrink: 0
  },
  listContainer: {
    height: '100%'
  }
}))

const OralHygieneReport = () => {
  const classes = useStyles()
  const { t } = useTranslation()

  const {
    accountOwnerId,
    pastXDays,
    setPastXDays,
    aggregationData,
    widgetCounts,
    isAggregationLoading,
    isAggregationError
  } = useOralHygieneReport()
  const canViewPatients = useCanViewCopilotPatients()

  const [selectedListType, setSelectedListType] = useState(OralHygieneListTypes.HygieneTrend)
  const [selectedCategories, setSelectedCategories] = useState([])

  const handleListTypeSelected = useCallback(
    listType => {
      trackEvent('Oral Hygiene Report - Patient view changed', { view: listType, pastXDays })
      setSelectedListType(listType)
      // Switching views clears the category filters of the newly opened view.
      setSelectedCategories([])
    },
    [pastXDays]
  )

  const handleSeePatientsClicked = useCallback(
    widget => {
      // A number card opens its view with the counted category preselected; the trend widget opens All.
      const target = getOralHygieneSeePatientsTarget(widget)
      if (!target) {
        return
      }
      const { listType, categories } = target
      trackEvent('Oral Hygiene Report - Widget - See Patients Clicked', {
        widget,
        view: listType,
        selectedCategories: categories,
        pastXDays
      })
      setSelectedListType(listType)
      setSelectedCategories(categories)
    },
    [pastXDays]
  )

  const handlePastXDaysChanged = useCallback(
    updatedPastXDays => {
      trackEvent('Oral Hygiene Report - Time range clicked', { time: updatedPastXDays, view: selectedListType })
      // Changing the date keeps the selected patient view and category filters.
      setPastXDays(updatedPastXDays)
    },
    [setPastXDays, selectedListType]
  )

  // Each card's view and category come from its See patients target, so the highlight and the list it opens agree.
  const numberCards = [
    { key: OralHygieneWidgets.HighCavityRisk, icon: <CavityRiskIcon /> },
    { key: OralHygieneWidgets.HighPerioRisk, icon: <PerioRiskIcon /> },
    { key: OralHygieneWidgets.HygienePayAttention, icon: <HygieneAttentionIcon /> }
  ].map(card => ({ ...card, value: widgetCounts[card.key], ...getOralHygieneSeePatientsTarget(card.key) }))

  return (
    <div className={classes.page}>
      <CopilotReportsNav activeReport="oralHygiene" />
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.titleRow}>
            <DazzedHeading18 className={classes.title}>{t('pages.oralHygieneReport.mainPage.title')}</DazzedHeading18>
            <BetaBadge className={classes.betaBadge} />
          </div>
          <Grid container className={classes.dateFilterContainer}>
            <Grid item>
              <DateFilter pastXDays={pastXDays} onChange={handlePastXDaysChanged} />
            </Grid>
          </Grid>
        </div>

        <Grid container id="oral-hygiene-widgets" wrap="nowrap" className={classes.widgetsContainer}>
          <Grid container direction="column" item xs={4} wrap="nowrap" className={classes.leftColumn}>
            <Grid item className={classes.trendWidgetContainer}>
              <HygieneTrendWidget
                counts={widgetCounts.hygieneTrend}
                isLoading={isAggregationLoading}
                isError={isAggregationError}
                isSelected={selectedListType === OralHygieneListTypes.HygieneTrend}
                onSeePatients={() => handleSeePatientsClicked(OralHygieneWidgets.HygieneTrend)}
              />
            </Grid>
            {numberCards.map(card => (
              <Grid item key={card.key} className={classes.numberCardContainer}>
                <OralHygieneNumberCard
                  label={t(`pages.oralHygieneReport.widgets.${card.key}.title`)}
                  tooltip={t(`pages.oralHygieneReport.widgets.${card.key}.tooltip`)}
                  icon={card.icon}
                  value={card.value}
                  isLoading={isAggregationLoading}
                  isError={isAggregationError}
                  isSelected={
                    selectedListType === card.listType && _.xor(selectedCategories, card.categories).length === 0
                  }
                  onSeePatients={() => handleSeePatientsClicked(card.key)}
                />
              </Grid>
            ))}
          </Grid>
          <Grid container direction="column" item xs={8} wrap="nowrap" className={classes.listContainer}>
            <Grid item style={{ flexGrow: 1, height: '100%' }}>
              {canViewPatients ? (
                <OralHygienePatientsList
                  accountOwnerId={accountOwnerId}
                  listType={selectedListType}
                  onListTypeSelected={handleListTypeSelected}
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                  pastXDays={pastXDays}
                  aggregationData={aggregationData}
                  isAggregationLoading={isAggregationLoading}
                />
              ) : (
                <PatientsListRestricted />
              )}
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

export default OralHygieneReport
